import React from 'react';
import { File, Download, Trash2, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Pending Review' },
  approved: { icon: CheckCircle, color: 'text-[#059669]', bg: 'bg-[#059669]/10', label: 'Approved' },
  rejected: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Rejected' },
};

const DocumentListItem = ({ 
  document, 
  onDelete, 
  onDownload,
  showStatus = true,
  allowDelete = true,
  isDeleting = false
}) => {
  const statusConfig = STATUS_CONFIG[document.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div 
      className="bg-[#0b1310] border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors"
      data-testid={`document-item-${document.id}`}
    >
      <div className="flex items-start gap-3">
        {/* File Icon */}
        <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
          <File className="w-5 h-5 text-[#059669]" />
        </div>
        
        {/* File Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white text-sm font-medium truncate">{document.file_name}</p>
            {showStatus && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${statusConfig.bg} ${statusConfig.color}`}>
                <StatusIcon className="w-3 h-3" />
                {statusConfig.label}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            {document.document_type && (
              <span className="capitalize">{document.document_type.replace(/_/g, ' ')}</span>
            )}
            {document.file_size && (
              <>
                <span>•</span>
                <span>{formatFileSize(document.file_size)}</span>
              </>
            )}
            {document.uploaded_at && (
              <>
                <span>•</span>
                <span>{formatDate(document.uploaded_at)}</span>
              </>
            )}
          </div>
          
          {/* Rejection reason */}
          {document.status === 'rejected' && document.rejection_reason && (
            <div className="mt-2 flex items-start gap-2 text-xs text-red-400 bg-red-500/10 rounded p-2">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{document.rejection_reason}</span>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {document.file_url && (
            <button
              onClick={() => onDownload?.(document)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Download"
              data-testid={`download-btn-${document.id}`}
            >
              <Download className="w-4 h-4" />
            </button>
          )}
          
          {allowDelete && onDelete && (
            <button
              onClick={() => onDelete(document)}
              disabled={isDeleting}
              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
              title="Remove"
              data-testid={`delete-btn-${document.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentListItem;
